import React from "react";
import { classNames } from "repoV2/utils/common/render/classNames";
import { HtmlHeader } from "repoV2/components/common/HtmlHeader/HtmlHeader";
import { LISTING_STATUSES } from "repoV2/features/Listing/Listing.constants";
import { PageBase } from "repoV2/components/common/PageBase/PageBase";
import commonStyles from "repoV2/features/CheckoutPages/CheckoutCommonStyles.module.scss";
import { getFaviconUrl } from "repoV2/components/common/HtmlHeader/HtmlHeader.utils";
import { CUSTOM_CODES_POSITION } from "repoV2/constants/customCodes/customCodes.constants";
import { CustomCode } from "repoV2/components/customCode/CustomCode";
import { getOptimisedImageUrl } from "repoV2/features/Common/modules/ExlyImage/utils/getOptimisedImageUrl";
import { checkout_page_listing_cover_img_width } from "repoV2/features/CheckoutPages/constants/CheckoutPages.constants";
import { DescriptionSection } from "../DescriptionSection/DescriptionSection";
import { TitleSection } from "../TitleSection/TitleSection";
import FooterSection from "../FooterSection/FooterSection";
import OverallCheckoutContextProvider from "../OverallCheckoutContextProvider";
import CheckoutRecentSalesPopup from "../CheckoutRecentSalesPopup/CheckoutRecentSalesPopup";
import useFetchNSetCheckoutPageSessionsSection from "../../utils/useFetchNSetCheckoutPageSessionsSection";
import useFetchNSetCheckoutPagePartPayments from "../../utils/useFetchNSetCheckoutPagePartPayments";
import CheckoutPaymentGateways from "../CheckoutPaymentGateways/CheckoutPaymentGateways";
import useFetchNSetCheckoutPagePriceSection from "../../utils/useFetchNSetCheckoutPagePriceSection";
import useFetchNSetCheckoutPageCustomQuestions from "../../utils/useFetchNSetCheckoutPageCustomQuestions";
import CheckoutFormSection from "../CheckoutFormSection/CheckoutFormSection";
import CheckoutStylesFromHostTheme from "../CheckoutStylesFromHostTheme/CheckoutStylesFromHostTheme";
import CheckoutFormButton from "../CheckoutFormButton/CheckoutFormButton";
import CheckoutRootFormWrapper from "../CheckoutRootFormWrapper/CheckoutRootFormWrapper";
import { ICheckoutListingPage } from "../CheckoutListingPage/ICheckoutListingPage.interfaces";
import styles from "../CheckoutListingPage/CheckoutListingPage.module.scss";

const CheckoutListingPageBase = (props: ICheckoutListingPage.IProps) => {
    const {
        hostName,
        listingSlug,
        listingDetails,
        creatorDetails,
        merchandiseDetails,
        initBooking,
        thankyouUuid,
        renewSubscription,
        communityUuid,
        dynamicLinkUuid,
        isPreview: isCheckoutPagePreviewMode,
        showExpressInterestFormInitalValue,
    } = props;

    const {
        default_listing_title = "", // This is the listing title that user set at the time of listing creation
        uuid: listingUuid = "",
        type: listingType,
        status: listingStatus = LISTING_STATUSES.LIVE, // listing status
        custom_codes = [],
    } = listingDetails;
    const {
        display_image: displayImage,
        social_info: socialInfo,
        payment_link_appearance: paymentLinkAppearance = {},
    } = creatorDetails;
    const { tab_assets: tabAssets = {} } = paymentLinkAppearance;
    const favicon = getFaviconUrl({
        pageSpecificFavIcon: tabAssets?.tab_icon || "",
        creatorSiteFaviconImgSrc: socialInfo?.favicon,
        profileImgSrc: displayImage,
        hostName,
    });

    useFetchNSetCheckoutPagePriceSection({
        hostName,
        listingUuid,
        listingSlug,
        thankyouUuid,
        communityUuid,
        dynamicLinkUuid,
    });

    // This 'showExpressInterestForm', is the actual check of whether to show 'express interest' form to
    // the customer or not. It takes the backend key for this, and checks if the listing is of type
    // with slots or classes. If yes and there are no classes or slots, then also it is express interest.
    // If the listing is not of type with slots and classes, the we use the backend key as it is.
    const { showExpressInterestForm } = useFetchNSetCheckoutPageSessionsSection(
        {
            hostName,
            listingSlug,
            listingUuid,
            listingType,
            listingStatus,

            showExpressInterest: listingDetails.is_express_interest,
            showExpressInterestFormInitalValue,
        }
    );
    useFetchNSetCheckoutPageCustomQuestions({
        hostName,
        listingUuid,
        listingSlug,
    });

    useFetchNSetCheckoutPagePartPayments({
        listingUuid,
        listingType,
        listingStatus,
        paymentPlanType: listingDetails?.payment_plan_type,
    });

    // Need to show payment modal in case of init_booking and renew_subscription
    const [showPaymentModal, setShowPaymentModal] = React.useState(
        initBooking || renewSubscription
    );
    const handleShowPaymentModal = () => {
        setShowPaymentModal(true);
    };
    const handleClosePaymentModal = () => {
        setShowPaymentModal(false);
    };

    return (
        <PageBase hostName={hostName}>
            <OverallCheckoutContextProvider
                listingDetails={listingDetails}
                creatorDetails={creatorDetails}
                merchandiseDetails={merchandiseDetails}
                listingSlug={listingSlug}
                hostName={hostName}
                showExpressInterestForm={Boolean(showExpressInterestForm)}
                isRenewSubscription={renewSubscription}
                thankyouUuid={thankyouUuid}
                communityUuid={communityUuid}
                dynamicLinkUuid={dynamicLinkUuid}
                isCheckoutPagePreviewMode={isCheckoutPagePreviewMode}
            >
                <HtmlHeader
                    title={tabAssets.tab_title || default_listing_title}
                    favicon={favicon}
                >
                    <link
                        rel="preload"
                        as="image"
                        href={getOptimisedImageUrl({
                            imageUrl: listingDetails?.cover_image || "",
                            fetchWidth: checkout_page_listing_cover_img_width,
                        })}
                    />
                </HtmlHeader>
                {/* Todo: @dev, uncomment this in Checkout when to give Custom code functionality */}
                {CustomCode({
                    position: CUSTOM_CODES_POSITION.HEAD,
                    codes: custom_codes,
                })}
                <CheckoutStylesFromHostTheme />
                <CheckoutPaymentGateways />
                <CheckoutRootFormWrapper>
                    <div className={styles.root}>
                        <section className={styles.mainContentWrapper}>
                            {/* Todo: @dev, uncomment this in Checkout when to give Custom code functionality */}
                            <CustomCode
                                position={
                                    CUSTOM_CODES_POSITION.MAIN_CONTENT_START
                                }
                                codes={custom_codes}
                            />
                            <section className={styles.bodySectionsContainer}>
                                <section
                                    className={classNames(
                                        styles.basicDetailsContainer,
                                        // Incase the modal is open, we need to hide the basic details and footer section
                                        // from ui, only on the mobile screen
                                        // This is done so on initail load, when the shoPaymentModal prop is true,
                                        // then we have to show the modal in open state, so no point in rendering this section then
                                        // as it will only increase the blocking time
                                        showPaymentModal &&
                                            commonStyles.hideInMobile
                                    )}
                                >
                                    <div className={styles.detailsSection}>
                                        <TitleSection />
                                        <DescriptionSection
                                            showPaymentModal={showPaymentModal}
                                        />
                                        <div className="flex-grow-1" />
                                    </div>
                                </section>
                                <section
                                    className={classNames(
                                        styles.paymentSummaryContainer
                                    )}
                                >
                                    <div
                                        className={classNames(
                                            styles.paymentSection
                                        )}
                                    >
                                        <CheckoutFormSection
                                            showPaymentModal={showPaymentModal}
                                            handleClosePaymentModal={
                                                handleClosePaymentModal
                                            }
                                        />
                                    </div>
                                </section>
                            </section>
                            <section
                                className={classNames(
                                    styles.footerSection,
                                    // Incase the modal is open, we need to hide the basic details and footer section
                                    // from ui, only on the mobile screen
                                    // This is done so on initail load, when the shoPaymentModal prop is true,
                                    // then we have to show the modal in open state, so no point in rendering this section then
                                    // as it will only increase the blocking time
                                    showPaymentModal &&
                                        commonStyles.hideInMobile
                                )}
                            >
                                <FooterSection />
                            </section>
                            {/* Todo: @dev, uncomment this in Checkout when to give Custom code functionality */}
                            <CustomCode
                                position={
                                    CUSTOM_CODES_POSITION.MAIN_CONTENT_END
                                }
                                codes={custom_codes}
                            />
                        </section>
                        <section
                            className={classNames(
                                styles.paymentSubmitFooter,
                                commonStyles.hideInDesktop,
                                // Incase the modal is open, we need to hide the basic details and footer section
                                // from ui, only on the mobile screen
                                // This is done so on initail load, when the shoPaymentModal prop is true,
                                // then we have to show the modal in open state, so no point in rendering this section then
                                // as it will only increase the blocking time
                                showPaymentModal && commonStyles.hideInMobile
                            )}
                        >
                            <CheckoutFormButton
                                handleShowPaymentModal={handleShowPaymentModal}
                            />
                        </section>
                    </div>
                </CheckoutRootFormWrapper>
                <CheckoutRecentSalesPopup />
            </OverallCheckoutContextProvider>
        </PageBase>
    );
};

export default CheckoutListingPageBase;
